import { forwardRef, useCallback, useRef, type HTMLAttributes } from 'react';
import { setFrameSize, NARROW, XNARROW, SHORT, XSHORT } from '../frameSize';

/**
 * The .phone-frame panel — full-screen on a phone, a 414x736 box on desktop.
 * Measures itself and stamps the pf-* size classes that App.css keys its layout
 * rules off, so the desktop preview and a real phone resolve the same rules.
 * See frameSize.ts for why this isn't a CSS container query.
 */
/**
 * The size classes as best we can know them before the panel exists.
 *
 * The ResizeObserver below is the authority once mounted, but it can only fire
 * AFTER first paint — and until it does the element carries no pf-* class at
 * all, so every phone-layout rule falls back to its desktop branch. On a phone
 * that is a desktop layout on screen, for as long as it takes the observer to
 * run. Seeding from the viewport makes the first paint right: full-screen on a
 * phone, and on desktop the panel is narrower than the window, so the observer
 * only ever narrows these further.
 */
function seedClasses() {
  if (typeof window === 'undefined') return '';
  // data-phone (index.html) is the device's own answer, and on a phone the
  // panel is the whole screen — so the phone layout applies even when the
  // viewport lies about its width, which "Request desktop site" makes it do.
  if (document.documentElement.hasAttribute('data-phone')) {
    return `pf-narrow pf-xnarrow${window.innerHeight <= SHORT ? ' pf-short' : ''}${window.innerHeight <= XSHORT ? ' pf-xshort' : ''}`;
  }
  const w = window.innerWidth;
  const h = window.innerHeight;
  return [
    w <= NARROW ? 'pf-narrow' : '',
    w <= XNARROW ? 'pf-xnarrow' : '',
    h <= SHORT ? 'pf-short' : '',
    h <= XSHORT ? 'pf-xshort' : '',
  ].filter(Boolean).join(' ');
}

const PhoneFrame = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  function PhoneFrame({ className, children, ...rest }, ref) {
    const observer = useRef<ResizeObserver | null>(null);

    // Callback ref: the panel is remounted by several routes, and each one needs
    // its own observer.
    const attach = useCallback((el: HTMLDivElement | null) => {
      observer.current?.disconnect();
      observer.current = null;

      if (el) {
        const phone = document.documentElement.hasAttribute('data-phone');
        const apply = (w: number, h: number) => {
          el.classList.toggle('pf-narrow', phone || w <= NARROW);
          el.classList.toggle('pf-xnarrow', phone || w <= XNARROW);
          el.classList.toggle('pf-short', h <= SHORT);
          el.classList.toggle('pf-xshort', h <= XSHORT);
          setFrameSize(w, h);
        };
        // contentBoxSize excludes the safe-area padding, so the height test is
        // against the space the app can actually draw in.
        const ro = new ResizeObserver((entries) => {
          const box = entries[0]?.contentBoxSize?.[0];
          if (box) apply(box.inlineSize, box.blockSize);
          else apply(el.clientWidth, el.clientHeight);
        });
        ro.observe(el); // fires immediately, so no separate initial measure
        observer.current = ro;
      }

      if (typeof ref === 'function') ref(el);
      else if (ref) ref.current = el;
    }, [ref]);

    return (
      <div
        {...rest}
        ref={attach}
        className={['phone-frame', seedClasses(), className].filter(Boolean).join(' ')}
      >
        {children}
      </div>
    );
  }
);

export default PhoneFrame;
